import React, { useEffect, useState } from 'react';
import MaterialTable, { MTableFilterRow, MTableToolbar } from 'material-table';
import LogsDataService from '../../services/LogService';
import Grid from '@material-ui/core/Grid';
import 'date-fns';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime, Settings } from 'luxon';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { getAppTimezone } from '../../utils/timezone';

Settings.defaultZone = 'Asia/Istanbul';

const useStyles = makeStyles(theme => ({
    button: {
        marginLeft: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    grid: { marginLeft: theme.spacing(2) },
    toolbar: { marginLeft: theme.spacing(10) },
    absoluteContainer: {
        position: 'absolute',
        zIndex: 9,
        width: '45vw',
        [theme.breakpoints.down('md')]: {
            position: 'initial',
        },
    },
}));

const EventsTable = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const [events, setLog] = useState([]);
    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 50,
        totalCount: 0,
    });

    // Get user role from localStorage as a simple string
    const [isAnalyzer, setIsAnalyzer] = useState(false);

    // Initialize user role from localStorage when component mounts
    useEffect(() => {
        const userRole = localStorage.getItem('level');
        // Check if the role is 'analyzer'
        if (userRole) {
            setIsAnalyzer(userRole === 'analyzer');
        }
    }, []);

    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });

    const tableRef = React.createRef();

    useEffect(() => {
        retrieveEvents();
    }, []);

    const [isLoading, setIsLoading] = useState(false);

    const retrieveEvents = () => {
        setIsLoading(true);
        LogsDataService.getAll()
            .then(response => {
                setLog(response.data);
                console.log(response.data);
                setIsLoading(false);
            })
            .catch(e => {
                console.log(e);
                setIsLoading(false);
            });
    };

    JSON.stringify(events, function(key, room) {
        // if room is null, return "" as a replacement
        if (room === null) {
            return 'empty';
        }

        // otherwise, leave the room unchanged
        return room;
    });

    const retrieveFilteredEvents = resolve => {
        setIsLoading(true);

        // Format the start date
        let formattedStartDate;
        if (selectedStartDate) {
            formattedStartDate = dayjs(selectedStartDate).format('YYYY-MM-DD');
        } else {
            formattedStartDate = dayjs().format('YYYY-MM-DD');
        }

        // Format the end date
        let formattedEndDate;
        if (selectedEndDate) {
            formattedEndDate = dayjs(selectedEndDate).format('YYYY-MM-DD');
        } else {
            formattedEndDate = dayjs().format('YYYY-MM-DD');
        }

        LogsDataService.getAllDateFilter(formattedStartDate, formattedEndDate)
            .then(response => {
                setLog(response.data);
                console.log(response.data);
                tableRef.current.onQueryChange();
                if (resolve) resolve();
                setIsLoading(false);
            })
            .catch(e => {
                console.log(e);
                setIsLoading(false);
            });
    };

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const handleEventRemove = (Event, resolve) => {
        LogsDataService.remove(Event.id)
            .then(res => {
                const dataRemove = [...events];
                const index = Event.tableData.id;
                dataRemove.splice(index, 1);
                setLog([...dataRemove]);
                resolve();
            })
            .catch(error => {
                setErrorMessages(['Remove failed! Server error']);
                setIserror(true);
            });
    };

    // Extend dayjs with plugins
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Function to get date format and locale based on current language
    const getDateFormatAndLocale = () => {
        const language = i18n.language;
        switch (language) {
            case 'en':
                return {
                    format: 'MM/DD/YYYY HH:mm:ss',
                    locale: 'en',
                    dateFormatExport: date => {
                        return `${date.getMonth() +
                            1}/${date.getDate()}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'es':
                return {
                    format: 'DD/MM/YYYY HH:mm:ss',
                    locale: 'es',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'bg':
            default:
                return {
                    format: 'DD.MM.YYYY г., HH:mm:ss ч.',
                    locale: 'bg',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}.${date.getFullYear()} г., ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')} ч.`;
                    },
                };
        }
    };

    // Get the current date format configuration
    const [dateConfig, setDateConfig] = useState(getDateFormatAndLocale());

    // Update date config when language changes
    useEffect(() => {
        setDateConfig(getDateFormatAndLocale());
    }, [i18n.language]);

    // * Add Filters to State Start
    const [selectedRows, setSelectedRows] = useState([]);

    // * Date Hooks
    var startDate = DateTime.local().startOf('day');

    const [selectedStartDate, handleStartDateChange] = useState(null);

    var endDate = DateTime.local().endOf('day');
    const [selectedEndDate, handleEndDateChange] = useState(null);

    const [components] = useState({
        Toolbar: props => (
            <div>
                <MTableToolbar {...props} />
            </div>
        ),
        FilterRow: props => <MTableFilterRow {...props} />,
    });

    const [columns] = useState([
        {
            title: t('reports.rawEventsReport.date'),
            field: 'time',
            type: 'date',
            dateSetting: { locale: 'bg-BG', hour12: false },
            filtering: false,
            render: rowData =>
                rowData.time
                    ? dayjs(rowData.time)
                          .tz(getAppTimezone())
                          .format('DD.MM.YYYY')
                    : '',
        },
        {
            title: t('reports.rawEventsReport.time'),
            field: 'time',
            type: 'time',
            dateSetting: { locale: 'bg-BG', hour12: false },
            filtering: false,
            render: rowData =>
                rowData.time
                    ? dayjs(rowData.time)
                          .tz(getAppTimezone())
                          .format('HH:mm:ss')
                    : '',
        },
        {
            title: t('reports.rawEventsReport.topic'),
            field: 'topic',
        },
        {
            title: t('reports.rawEventsReport.message'),
            field: 'message',
            editable: 'never',
        },
        {
            title: t('reports.rawEventsReport.location'),
            field: 'room',
            editable: 'never',
            cellStyle: (e, rowData) => {
                if (rowData.room === 'empty') {
                    return { backgroundColor: '#FFCCCC' };
                }
            },
        },
    ]);
    // * Add Filters to State End

    return (
        <div>
            <Grid
                container
                spacing={1}
                className={classes.absoluteContainer}
                style={{ paddingLeft: '16px' }}
            >
                <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={dateConfig.locale}
                    >
                        <MobileDatePicker
                            margin="normal"
                            closeOnSelect={true}
                            id="selectedStartDate"
                            label={t('reports.common.startDate')}
                            defaultValue={dayjs()}
                            ampm={false}
                            value={selectedStartDate}
                            onChange={date => handleStartDateChange(date)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={dateConfig.locale}
                    >
                        <MobileDatePicker
                            margin="normal"
                            closeOnSelect={true}
                            id="selectedEndDate"
                            label={t('reports.common.endDate')}
                            defaultValue={dayjs()}
                            value={selectedEndDate}
                            onChange={handleEndDateChange}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4} lg={6}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={retrieveFilteredEvents}
                    >
                        {isLoading
                            ? t('reports.common.loading')
                            : t('reports.common.filter')}
                    </Button>
                </Grid>
            </Grid>
            <MaterialTable
                title={t('reports.rawEventsReport.title')}
                columns={columns}
                data={events}
                isLoading={isLoading}
                style={{
                    borderRadius: '0',
                    boxShadow: 'none',
                }}
                actions={[
                    {
                        icon: 'refresh',
                        tooltip: t('reports.common.refresh'),
                        isFreeAction: true,
                        onClick: retrieveEvents,
                    },
                ]}
                options={{
                    loadingType: 'linear',
                    pageSize: 50,
                    maxBodyHeight: '68vh',
                    showTitle: false,
                    sorting: true,
                    actionsColumnIndex: -1,
                    exportButton: !isAnalyzer
                        ? { csv: true, pdf: false }
                        : false,
                    filtering: false,
                    pageSizeOptions: [20, 50, 100, 200, 300, 400, 500],
                    search: true,
                    toolbar: true,
                }}
                components={components}
                onSelectionChange={rows => {
                    setSelectedRows(rows);
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows:
                            '{from}-{to} ' +
                            t('reports.common.of') +
                            ' {count}',
                        labelRowsPerPage: t('reports.common.rowsPerPage'),
                        labelRowsSelect: t('reports.common.rowsSelect'),
                    },
                    header: {
                        actions: t('reports.common.actions'),
                    },
                    body: {
                        emptyDataSourceMessage: t(
                            'reports.common.emptyDataSourceMessage'
                        ),
                        editTooltip: t('reports.common.edit'),
                        deleteTooltip: t('reports.common.delete'),
                        filterRow: {
                            filterTooltip: t('reports.common.filter'),
                        },
                        editRow: {
                            deleteText: t('reports.common.deleteConfirmation'),
                            cancelTooltip: t('reports.common.cancel'),
                            saveTooltip: t('reports.common.save'),
                        },
                    },
                    toolbar: {
                        exportTitle: t('reports.common.export'),
                        exportName: t('reports.common.exportCSV'),
                        searchTooltip: t('reports.common.search'),
                        searchPlaceholder: t(
                            'reports.common.searchPlaceholder'
                        ),
                        nRowsSelected: t('reports.common.rowsSelected', {
                            count: selectedRows.length,
                        }),
                    },
                }}
            />
        </div>
    );
};

export default EventsTable;
